<script lang="ts" setup>
import { Info } from "@lucide/vue";
import type { Cell, Column, Row } from "@tanstack/vue-table";

import type { TEI } from "@/lib/api-client";
import type { LocationWindowItem, WindowItem } from "@/types/global.ts";
import type { simpleTEIMetadata } from "@/types/teiCorpus";

interface Props {
	params: LocationWindowItem["params"];
}

const props = defineProps<Props>();
const { params } = toRefs(props);

const row = computed(() => {
	return params.value as unknown as Row<unknown>;
});

const rowOriginal = computed(() => {
	return row.value.original as PatchedFeatureType;
});

const allCells = computed(() => {
	return (row.value.getAllCells as () => Array<Cell<unknown, Array<PatchedFeatureType>>>)();
});

const columns = computed(() => {
	return allCells.value
		.filter((cell) => cell.getValue() && (cell.getValue() as Array<PatchedFeatureType>).length > 0)
		.map((cell) => ({ cell, column: cell.column as Column<PatchedFeatureType> }));
});

const citation = computed(() => {
	const author: Array<unknown> = [];
	const place = rowOriginal.value.properties.name;
	return {
		author,
		title: `${place}`,
	} as simpleTEIMetadata;
});

function getHighlightedValues(col: Column<PatchedFeatureType, unknown>) {
	return [...((col.getFilterValue() as Map<string, unknown>)?.keys() ?? [])];
}

function getNonFeatureValue(col: (typeof columns.value)[0]) {
	const value = rowOriginal.value.properties[col.column.columnDef.id!];
	if (!value || typeof value === "string") return value;
	return (value as unknown as Array<Record<string, string>>).map((entry) => entry.name).join(" / ");
}

const openOrUpdateWindow = useOpenOrUpdateWindow();
function onFeatureClick(col: Column<PatchedFeatureType, unknown>) {
	openOrUpdateWindow(
		{
			targetType: "FeatureStatistics",
			params: {
				featureId: col.id,
				showCitation: false,
			},
		} as unknown as WindowItem,
		`Feature: ${col.columnDef.header}`,
	);
}
function openProfile(profile: TEI) {
	openOrUpdateWindow(
		{
			targetType: "Profile",
			params: {
				textId: profile["@id"],
			},
		} as unknown as WindowItem,
		profile.teiHeader?.fileDesc.titleStmt.titles?.[0]?.$ ?? "",
		true,
	);
}

const showDetails = ref(true);
</script>

<template>
	<div class="relative isolate grid size-full overflow-auto">
		<div v-if="params.showCitation">
			<Citation :header="citation" type="entry" />
		</div>
		<div class="flex justify-end justify-items-end border-b bg-white p-2">
			<div class="flex gap-2">
				<Toggle
					v-model:model-value="showDetails"
					class="h-8 text-neutral-700"
					data-onboarding="show-details"
					><Info class="size-4 stroke-neutral-800 transition-colors" />
					<span class="line-clamp-1 text-ellipsis">Show details</span></Toggle
				>
			</div>
		</div>
		<Table>
			<TableBody>
				<TableRow v-if="rowOriginal.profiles && rowOriginal.profiles.length > 0">
					<TableCell class="capitalize">Profile</TableCell>
					<TableCell class="capitalize">
						<Button
							v-for="p in rowOriginal.profiles"
							:key="p['@id']"
							class="p-0"
							variant="link"
							@click="openProfile(p)"
						>
							{{ p.teiHeader?.fileDesc.titleStmt.titles?.map((t) => t.$).join(", ") }}
						</Button>
					</TableCell>
				</TableRow>
				<TableRow v-for="col in columns" :key="col.column.id">
					<TableCell class="capitalize"
						><Button
							v-if="col.column.id.startsWith('ft_')"
							class="h-auto shrink-0 p-0 font-normal text-black! text-left whitespace-normal"
							data-onboarding="feature-link"
							variant="link"
							@click="onFeatureClick(col.column)"
							>{{ col.column.columnDef.header }}</Button
						>
						<template v-else>{{ col.column.columnDef.header }}</template>
					</TableCell>
					<TableCell>
						<GeojsonTablePropertyCell
							v-if="!['name', 'country', 'alternateNames'].includes(col.column.id)"
							:column="col.column"
							:full-entry="rowOriginal.properties"
							:highlighted-values="getHighlightedValues(col.column)"
							:show-all-details="showDetails"
							:value="rowOriginal.properties[col.column.columnDef.id!]"
						></GeojsonTablePropertyCell>
						<span v-else class="font-normal">{{ getNonFeatureValue(col) }}</span>
					</TableCell>
				</TableRow>
			</TableBody>
		</Table>
	</div>
</template>
